import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, X, Search, Ban } from "lucide-react";
import {
  clientContactRelApi,
  customerApi,
  driverArtistRestrictionApi,
} from "@/lib/entities";
import { apiError } from "@/lib/errors";
import { useAuth } from "@/contexts/AuthContext";

/**
 * DAQ-1 Sub-Phase 3 · Driver ↔ Client (Artist) Eligibility tab.
 *
 * Lists active DO NOT ASSIGN restrictions for a driver against
 * specific clients. Reuses the CC1 searchable Client selector
 * (`/api/clients/search`) so the "Three Doors → 3 Doors Down" tokenized
 * match behaves identically to the rest of the app.
 *
 * There is no generic override control by design — Sub-Phase 3 forbids
 * override UI. Removing a restriction is a permission-gated write on
 * the canonical row itself.
 */

const WRITE_ROLES = ["super_admin", "admin", "ops_dispatch"];

export default function DriverClientEligibilityTab({ driverId }) {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [rows, setRows] = useState(null);
  const [customerNames, setCustomerNames] = useState({}); // id -> display_name
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await driverArtistRestrictionApi.list(driverId);
      r.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setRows(r);
      // Hydrate any customer_ids we haven't resolved yet.
      const missing = [...new Set(r.map((x) => x.customer_id))]
        .filter((cid) => cid && !customerNames[cid]);
      if (missing.length > 0) {
        const additions = {};
        await Promise.all(missing.map(async (cid) => {
          try {
            const cust = await customerApi.get(cid);
            additions[cid] = cust.display_name;
          } catch {
            additions[cid] = "(client not found)";
          }
        }));
        setCustomerNames((p) => ({ ...p, ...additions }));
      }
    } catch (e) {
      toast.error(apiError(e) || "Failed to load restrictions");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [driverId]);

  const remove = async (row) => {
    const name = customerNames[row.customer_id] || row.customer_id;
    if (!window.confirm(`Remove DO NOT ASSIGN restriction against "${name}"?`)) return;
    try {
      await driverArtistRestrictionApi.archive(driverId, row.id);
      toast.success("Restriction removed");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-3" data-testid="driver-client-eligibility-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11.5px] text-steel max-w-2xl leading-relaxed">
          Clients this driver may not be assigned to. Each active row hard-blocks the driver from any tour whose Client
          matches. Restrictions are stored on a canonical, auditable row — there is no operator-level override.
        </div>
        {canWrite && (
          <button
            data-testid="add-restriction-btn"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-critical-red text-white text-[12.5px] rounded-sm hover:bg-critical-red/90">
            <Ban size={13} /> Add DO NOT ASSIGN
          </button>
        )}
      </div>

      <div className="bg-white border border-coach-silver rounded-md overflow-x-auto">
        {rows === null ? (
          <div className="p-6 text-center text-steel text-[13px]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-steel text-[13px]" data-testid="restrictions-empty">
            No active client restrictions. This driver is eligible for every Client (subject to availability and credential status).
          </div>
        ) : (
          <table className="w-full text-[13px]" data-testid="restrictions-table">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-4 py-2">Client</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Internal Reason</th>
                <th className="text-left px-4 py-2 w-40 hidden md:table-cell">Effective Window</th>
                {canWrite && <th className="w-24 px-4 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`restriction-row-${r.id}`}
                    className="border-t border-coach-silver/60 hover:bg-road-white">
                  <td className="px-4 py-2">
                    <Link to={`/customers/${r.customer_id}`}
                          className="text-coach-blue hover:underline"
                          data-testid={`restriction-client-link-${r.id}`}>
                      {customerNames[r.customer_id] || "…"}
                    </Link>
                    <span className="ml-2 inline-flex items-center border border-critical-red/40 text-critical-red bg-critical-red/10 rounded-sm px-1.5 py-0.5 text-[10px] uppercase tracking-widest">
                      DO NOT ASSIGN
                    </span>
                  </td>
                  <td className="px-4 py-2 text-steel hidden md:table-cell">
                    {r.internal_reason || "—"}
                  </td>
                  <td className="px-4 py-2 tabular font-mono text-[12px] text-steel hidden md:table-cell">
                    {r.effective_from ? r.effective_from : "any"} → {r.effective_until ? r.effective_until : "any"}
                  </td>
                  {canWrite && (
                    <td className="px-4 py-2 text-right">
                      <button
                        data-testid={`restriction-remove-${r.id}`}
                        onClick={() => remove(r)}
                        className="inline-flex items-center gap-1 text-[12px] text-critical-red hover:underline">
                        <Trash2 size={12} /> Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <RestrictionDialog
          driverId={driverId}
          onClose={() => setCreating(false)}
          onSaved={async () => { setCreating(false); await load(); }}
        />
      )}
    </div>
  );
}

function RestrictionDialog({ driverId, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveUntil, setEffectiveUntil] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const r = await clientContactRelApi.searchClients(q, 20);
        if (!cancel) setResults(r);
      } catch { /* ignore */ }
    }, 220);
    return () => { cancel = true; clearTimeout(t); };
  }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return toast.error("Choose a Client");
    setBusy(true);
    try {
      await driverArtistRestrictionApi.create(driverId, {
        driver_id: driverId,
        customer_id: selected.id,
        internal_reason: reason.trim() || null,
        effective_from: effectiveFrom || null,
        effective_until: effectiveUntil || null,
      });
      toast.success("DO NOT ASSIGN restriction added");
      await onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 409 || /already exists/i.test(detail)) {
        toast.error("An active DO NOT ASSIGN already exists for this Client");
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-carbon/60 flex items-center justify-center px-4"
         data-testid="restriction-dialog"
         onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-md w-full max-w-md border border-coach-silver">
        <div className="bg-critical-red text-white px-4 py-3 flex items-center justify-between">
          <div className="text-[14px]">Add DO NOT ASSIGN Restriction</div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white">
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3 text-[13px]">
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">
              Client <span className="text-critical-red">*</span>
            </div>
            {selected ? (
              <div className="border border-coach-blue bg-coach-blue/5 rounded-sm px-2.5 py-1.5 flex items-center justify-between">
                <span className="text-carbon font-medium">{selected.display_name}</span>
                <button type="button" onClick={() => { setSelected(null); setQ(""); }}
                        className="text-[11px] text-coach-blue hover:underline">Change</button>
              </div>
            ) : (
              <div className="relative">
                <Search size={12} className="absolute left-2 top-2.5 text-steel" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Search Clients by name (e.g. Three Doors → 3 Doors Down)"
                       data-testid="restriction-client-search"
                       className="w-full border border-coach-silver rounded-sm pl-7 pr-2 py-1.5" />
                {results.length > 0 && (
                  <div className="mt-1 max-h-56 overflow-y-auto border border-coach-silver rounded-sm bg-white"
                       data-testid="restriction-client-results">
                    {results.map((r) => (
                      <button type="button" key={r.id} onClick={() => setSelected(r)}
                              data-testid={`restriction-client-option-${r.id}`}
                              className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-chrome-mist border-b border-coach-silver/50 last:border-0">
                        <span className="text-carbon">{r.display_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </label>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">Internal Reason</div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                      placeholder="Never shown to the customer. Explain why for the audit trail."
                      data-testid="restriction-reason"
                      className="w-full border border-coach-silver rounded-sm px-2 py-1.5" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">Effective From</div>
              <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
                     data-testid="restriction-effective-from"
                     className="w-full border border-coach-silver rounded-sm px-2 py-1.5" />
            </label>
            <label className="block">
              <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">Effective Until</div>
              <input type="date" value={effectiveUntil} onChange={(e) => setEffectiveUntil(e.target.value)}
                     data-testid="restriction-effective-until"
                     className="w-full border border-coach-silver rounded-sm px-2 py-1.5" />
            </label>
          </div>
          <div className="text-[11px] text-steel italic">
            Leave both effective dates blank for an open-ended restriction.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-coach-silver bg-road-white rounded-b-md">
          <button type="button" onClick={onClose} disabled={busy}
                  className="px-3 py-1.5 text-[12.5px] border border-coach-silver rounded-sm text-steel hover:bg-white">
            Cancel
          </button>
          <button type="submit" disabled={busy || !selected}
                  data-testid="restriction-submit"
                  className="px-3 py-1.5 text-[12.5px] bg-critical-red text-white rounded-sm hover:bg-critical-red/90 disabled:opacity-50">
            {busy ? "Saving…" : "Add Restriction"}
          </button>
        </div>
      </form>
    </div>
  );
}
